import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { KeyRound, Mail, Check, AlertCircle, Clock, Eye, EyeOff, RefreshCw, Search, Users, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminResetPasswords() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [matriculados, setMatriculados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Para reset manual
  const [emailBuscar, setEmailBuscar] = useState('');
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reseteando, setReseteando] = useState(false);
  const [buscando, setBuscando] = useState(false);
  
  // Para filtrar matriculados
  const [filtroMatriculado, setFiltroMatriculado] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [solicitudesRes, matriculadosRes] = await Promise.all([
        axios.get(`${API_URL}/admin/solicitudes-reset`),
        axios.get(`${API_URL}/admin/usuarios-con-cuenta`)
      ]);
      setSolicitudes(solicitudesRes.data);
      setMatriculados(matriculadosRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const buscarUsuario = async (e) => {
    e?.preventDefault();
    setError('');
    setUsuarioEncontrado(null);
    setBuscando(true);

    try {
      const response = await axios.get(`${API_URL}/admin/buscar-usuario?email=${encodeURIComponent(emailBuscar)}`);
      setUsuarioEncontrado(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Usuario no encontrado');
    } finally {
      setBuscando(false);
    }
  };

  const seleccionarMatriculado = (mat) => {
    setEmailBuscar(mat.email);
    setUsuarioEncontrado({
      id: mat.user_id,
      email: mat.email,
      rol: 'trabajador_social',
      trabajador_nombre: `${mat.nombre} ${mat.apellido}`
    });
    setError('');
    setSuccess('');
    setNuevaPassword('');
  };

  const resetearPassword = async () => {
    if (!usuarioEncontrado || !nuevaPassword) return;
    
    if (nuevaPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setReseteando(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/admin/reset-password`, {
        user_id: usuarioEncontrado.id,
        nueva_password: nuevaPassword
      });
      setSuccess(`Contraseña reseteada correctamente para ${usuarioEncontrado.email}`);
      setUsuarioEncontrado(null);
      setEmailBuscar('');
      setNuevaPassword('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al resetear contraseña');
    } finally {
      setReseteando(false);
    }
  };

  const marcarAtendida = async (solicitudId) => {
    try {
      await axios.put(`${API_URL}/admin/solicitudes-reset/${solicitudId}/atender`);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar matriculados
  const matriculadosFiltrados = matriculados.filter(mat => {
    if (!filtroMatriculado) return true;
    const busqueda = filtroMatriculado.toLowerCase();
    return (
      mat.nombre?.toLowerCase().includes(busqueda) ||
      mat.apellido?.toLowerCase().includes(busqueda) ||
      mat.email?.toLowerCase().includes(busqueda) ||
      mat.numero_matricula?.toLowerCase().includes(busqueda)
    );
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Reset de Contraseñas</h1>

      {/* Mensajes */}
      {error && (
        <div className="mb-6 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          <AlertCircle className="w-4 h-4" aria-hidden="true" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm" role="status">
          <Check className="w-4 h-4" aria-hidden="true" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Lista de Matriculados con Cuenta */}
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5" aria-hidden="true" />
              Matriculadxs con Cuenta
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {matriculados.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Buscar por nombre, email o matrícula..."
                value={filtroMatriculado}
                onChange={(e) => setFiltroMatriculado(e.target.value)}
                className="text-sm"
                aria-label="Filtrar matriculados"
              />
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" aria-hidden="true"></div>
                <span className="sr-only">Cargando...</span>
              </div>
            ) : matriculadosFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 text-gray-300" aria-hidden="true" />
                <p>No se encontraron matriculados</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto" role="list" aria-label="Lista de matriculados">
                {matriculadosFiltrados.map((mat) => (
                  <button
                    key={mat.user_id}
                    onClick={() => seleccionarMatriculado(mat)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      usuarioEncontrado?.id === mat.user_id
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    role="listitem"
                    aria-label={`Seleccionar a ${mat.nombre} ${mat.apellido}`}
                  >
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {mat.apellido}, {mat.nombre}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{mat.email}</p>
                    <p className="text-xs text-gray-400">Mat. {mat.numero_matricula}</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Manual */}
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="w-5 h-5" aria-hidden="true" />
              Resetear Contraseña
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={buscarUsuario} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-buscar">Buscar por email</Label>
                <div className="flex gap-2">
                  <Input
                    id="email-buscar"
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={emailBuscar}
                    onChange={(e) => setEmailBuscar(e.target.value)}
                    required
                  />
                  <Button type="submit" disabled={buscando} size="sm">
                    <Search className="w-4 h-4" aria-hidden="true" />
                    <span className="sr-only">Buscar</span>
                  </Button>
                </div>
              </div>
            </form>

            {usuarioEncontrado && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Usuario seleccionado:</p>
                  <p className="text-blue-900 font-semibold">{usuarioEncontrado.email}</p>
                  {usuarioEncontrado.trabajador_nombre && (
                    <p className="text-sm text-blue-600">{usuarioEncontrado.trabajador_nombre}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nueva-password">Nueva contraseña</Label>
                  <div className="relative">
                    <Input
                      id="nueva-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={nuevaPassword}
                      onChange={(e) => setNuevaPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={resetearPassword} 
                  disabled={reseteando || !nuevaPassword}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                  {reseteando ? 'Reseteando...' : 'Resetear Contraseña'}
                </Button>
              </div>
            )}

            {!usuarioEncontrado && (
              <div className="text-center py-8 text-gray-400">
                <KeyRound className="w-12 h-12 mx-auto mb-3 text-gray-200" aria-hidden="true" />
                <p className="text-sm">Seleccioná un matriculado de la lista o buscá por email</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Solicitudes Pendientes */}
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5" aria-hidden="true" />
              Solicitudes Pendientes
              {solicitudes.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  {solicitudes.length}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" aria-hidden="true"></div>
                <span className="sr-only">Cargando...</span>
              </div>
            ) : solicitudes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" aria-hidden="true" />
                <p>No hay solicitudes pendientes</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto" role="list" aria-label="Solicitudes pendientes">
                {solicitudes.map((sol) => (
                  <div 
                    key={sol.id} 
                    className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
                    role="listitem"
                  >
                    <div className="mb-2">
                      <p className="font-medium text-gray-900 text-sm">{sol.email}</p>
                      <p className="text-xs text-gray-500">{formatFecha(sol.fecha_solicitud)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => {
                          setEmailBuscar(sol.email);
                          buscarUsuario();
                        }}
                      >
                        <Search className="w-3 h-3 mr-1" aria-hidden="true" />
                        Buscar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs"
                        onClick={() => marcarAtendida(sol.id)}
                      >
                        <Check className="w-3 h-3 mr-1" aria-hidden="true" />
                        Atendida
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
